import { action, observable, runInAction, makeObservable } from "mobx";
import api from "../../api";
import { Opportunity } from "./Opportunity";

class OpportunityStore {
  opportunities: Opportunity[] = [];
  opportunity?: Opportunity = undefined;

  constructor() {
    makeObservable(this, {
      opportunities: observable,
      opportunity: observable,
      getOpportunities: action.bound,
      getOpportunity: action.bound,
      addOpportunity: action.bound,
      renewOpportunity: action.bound,
      editOpportunity: action.bound,
      deleteOpportunity: action.bound,
      addResellerToOpportunity: action.bound,
      removeResellerFromOpportunity: action.bound,
      addProductToOpportunity: action.bound,
      deleteProductFromOpportunity: action.bound
    });
  }

  async getOpportunities(applicationId: string) {
    try {
      const opportunities = await api.opportunities.retrieveOpportunities(applicationId);
      const converted = opportunities.map(Opportunity.convertFrom);

      runInAction(() => {
        this.opportunities = converted;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getOpportunity(applicationId: string, bidNumber: string, withSpecification: boolean) {
    try {
      const opportunity = await api.opportunities.getOpportunity(applicationId, bidNumber, withSpecification);
      const converted = Opportunity.convertFrom(opportunity);

      runInAction(() => {
        this.opportunity = converted;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async addOpportunity(
    applicationId: string,
    bidNumber: string,
    bidTitle: string,
    description: string,
    defaultResellers: string,
    startDate: string,
    endDate: string,
    quoteOnly: boolean
  ): Promise<boolean> {
    try {
      return await api.opportunities.addOpportunity(applicationId, bidNumber, bidTitle, description, defaultResellers, startDate, endDate, quoteOnly);
    } catch (e) {
      return false;
    }
  }

  async renewOpportunity(
    applicationId: string,
    bidNumber: string,
    newBidNumber: string,
    newBidTitle: string,
    newStartDate: string,
    newEndDate: string,
    quoteOnly: boolean
  ): Promise<boolean> {
    try {
      return await api.opportunities.renewOpportunity(applicationId, bidNumber, newBidNumber, newBidTitle, newStartDate, newEndDate, quoteOnly);
    } catch (e) {
      return false;
    }
  }

  async editOpportunity(
    applicationId: string,
    bidNumber: string,
    fieldId: string,
    newValue: string
  ): Promise<boolean> {
    try {
      return await api.opportunities.editOpportunity(applicationId, bidNumber, fieldId, newValue);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteOpportunity(applicationId: string, bidNumber: string): Promise<boolean> {
    try {
      return await api.opportunities.deleteOpportunity(applicationId, bidNumber);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async addResellerToOpportunity(applicationId: string, bidNumber: string, resellerId: string): Promise<boolean> {
    try {
      return await api.opportunities.addResellerToOpportunity(applicationId, bidNumber, resellerId);
    } catch (e) {
      return false;
    }
  }

  async removeResellerFromOpportunity(applicationId: string, bidNumber: string, resellerId: string): Promise<boolean> {
    try {
      return await api.opportunities.removeResellerFromOpportunity(applicationId, bidNumber, resellerId);
    } catch (e) {
      return false;
    }
  }

  async addProductToOpportunity(applicationId: string, bidNumber: string, randmarSKU: string, bidPrice: number, bidPriceType: string): Promise<boolean> {
    try {
      return await api.opportunities.addProductToOpportunity(applicationId, bidNumber, randmarSKU, bidPrice, bidPriceType);
    } catch (e) {
      return false;
    }
  }

  async deleteProductFromOpportunity(applicationId: string, bidNumber: string, randmarSKU: string) {
    try {
      return await api.opportunities.deleteProductFromOpportunity(applicationId, bidNumber, randmarSKU);
    } catch (e) {
      return false;
    }
  }
}

export default OpportunityStore;
